import { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { EntranceDirectionIndicator } from '../components/legacy-ball/table-management/EntranceDirectionIndicator';
import { MiniFloorplanMap } from '../components/legacy-ball/table-management/MiniFloorplanMap';

interface GuestCardData {
  fullName: string;
  ticketNumber: string;
  registrationId: string | null;
  tierName: string | null;
  tableName: string | null;
  tableNumber: number | null;
  seatNumber: number | null;
  zoneName: string | null;
  entryGuidance: string | null;
  orderReferenceId: string | null;
  chapterAffiliation: string | null;
  mealSelection: string | null;
  isCheckedIn: boolean;
  checkedInAt: string | null;
  suggestedEntrance: string | null;
  entranceDirection: string | null;
  gridAddress: string | null;
  wayfindingInstructions: string | null;
}

interface OrderGuest {
  ticketNumber: string;
  fullName: string;
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://kdu-hub-production.up.railway.app';

export function MyInfoLookupPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMode, setSearchMode] = useState<'ticket' | 'order' | 'name'>('ticket');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<GuestCardData | null>(null);
  const [orderGuests, setOrderGuests] = useState<OrderGuest[]>([]);
  const [showMap, setShowMap] = useState(false);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setLoading(true);
    setError('');
    setData(null);
    setOrderGuests([]);
    try {
      if (searchMode === 'ticket') {
        const res = await fetch(`${API_BASE}/guest-card/search?q=${encodeURIComponent(searchTerm.trim())}`);
        if (!res.ok) { setError(res.status === 404 ? 'Ticket not found. Please check the number.' : 'Search failed.'); return; }
        setData(await res.json());
      } else if (searchMode === 'order') {
        const res = await fetch(`${API_BASE}/guest-card/order-guests?orderRef=${encodeURIComponent(searchTerm.trim())}`);
        if (!res.ok) { setError(res.status === 404 ? 'Order not found. Please check the number.' : 'Search failed.'); return; }
        const json = await res.json();
        setOrderGuests(json.guests || []);
      } else {
        const res = await fetch(`${API_BASE}/guest-card/search-by-name?name=${encodeURIComponent(searchTerm.trim())}`);
        if (!res.ok) { setError(res.status === 404 ? 'No guests found with that name.' : 'Search failed.'); return; }
        const json = await res.json();
        setOrderGuests(json.guests || []);
      }
    } catch { setError('Unable to connect. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleSelectGuest = async (ticketNumber: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/guest-card/${encodeURIComponent(ticketNumber)}`);
      if (!res.ok) { setError('Could not load guest info.'); return; }
      setData(await res.json());
      setOrderGuests([]);
    } catch { setError('Unable to connect.'); }
    finally { setLoading(false); }
  };

  const handleReset = () => {
    const lastFullName = data?.fullName || '';
    setData(null);
    setOrderGuests([]);
    setError('');
    setSearchMode('name');
    setSearchTerm(lastFullName);
  };

  // Show the info card
  if (data) {
    return (
      <div style={styles.page}>
        <div style={{ width: '100%', maxWidth: '420px', backgroundColor: '#fff', borderRadius: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden', position: 'relative', zIndex: 1 }}>
          <div style={{ position: 'relative', zIndex: 1 }}>

          {/* Title bar with Order # */}
          <div style={{ padding: '14px 20px', backgroundColor: '#1a6b3c', color: '#fff', fontSize: '0.85rem', fontWeight: 600 }}>
            REF/ORDER | {data.orderReferenceId || data.ticketNumber}
          </div>

          {/* Content area */}
          <div style={{ padding: '20px' }}>

            {/* Row 1: Name (left) + Tier (right) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f2d1f', textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>{data.fullName}</div>
              {data.tierName && <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#7b1fa2', backgroundColor: '#f3e5f5', padding: '3px 10px', borderRadius: '10px' }}>{data.tierName}</span>}
            </div>

            {/* Row 2: Chapter (left) + Zone (right) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '0.85rem', color: '#333' }}>Chapter: {data.chapterAffiliation || '—'}</span>
              {data.zoneName && <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1565c0' }}>Zone: {data.zoneName}</span>}
            </div>

            {/* QR Code — centered with spacing */}
            {data.registrationId && (
              <div style={{ textAlign: 'center', margin: '16px 0' }}>
                <QrCodeDisplay registrationId={data.registrationId} />
              </div>
            )}

            {/* Spacer + Separator */}
            <div style={{ height: '16px' }} />
            <hr style={{ border: 'none', borderTop: '2px solid #e5e7eb', margin: '0 0 16px' }} />

            {/* Table Name (prominent) */}
            {(data.tableName || data.tableNumber != null) && (
              <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#0f2d1f', textTransform: 'uppercase', letterSpacing: '0.01em', lineHeight: 1.15, marginBottom: '10px' }}>
                {data.tableName || `Table ${data.tableNumber}`}
              </div>
            )}

            {/* Table # pill + Seat # pill + Ticket # pill (side by side, 2-line each, larger) */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
              {data.tableNumber != null && (
                <span style={{ padding: '12px 22px', borderRadius: '16px', backgroundColor: '#1a6b3c', color: '#fff', textAlign: 'center', lineHeight: 1.2 }}>
                  <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 400 }}>Table</span>
                  <span style={{ display: 'block', fontSize: '1.5rem', fontWeight: 800 }}>{data.tableNumber}</span>
                </span>
              )}
              {data.seatNumber != null && (
                <span style={{ padding: '12px 22px', borderRadius: '16px', backgroundColor: '#0d47a1', color: '#fff', textAlign: 'center', lineHeight: 1.2 }}>
                  <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 400 }}>Seat</span>
                  <span style={{ display: 'block', fontSize: '1.5rem', fontWeight: 800 }}>{data.seatNumber}</span>
                </span>
              )}
              <span style={{ padding: '12px 22px', borderRadius: '16px', backgroundColor: '#7b1fa2', color: '#fff', textAlign: 'center', lineHeight: 1.2, flex: 1, minWidth: '100px' }}>
                <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 400 }}>Ticket</span>
                <span style={{ display: 'block', fontSize: '1.5rem', fontWeight: 800 }}>{data.ticketNumber}</span>
              </span>
            </div>

            {/* Check-in status */}
            <div style={{ fontSize: '0.88rem', fontWeight: 600, color: data.isCheckedIn ? '#16a34a' : '#d97706' }}>
              {data.isCheckedIn ? '✅ Checked in' : '⬜ Not checked in yet'}
            </div>

            {/* Suggested Entrance (wayfinding) */}
            {(data.wayfindingInstructions || data.suggestedEntrance) && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 14px' }}>
                <span style={{ fontSize: '1rem', flexShrink: 0 }}>🧭</span>
                <div>
                  {data.wayfindingInstructions ? (
                    <div style={{ fontSize: '0.82rem', color: '#1e40af', lineHeight: 1.5 }}>{data.wayfindingInstructions}</div>
                  ) : (
                    <>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e40af' }}>Enter via: {data.suggestedEntrance}</div>
                      {data.entranceDirection && (
                        <div style={{ fontSize: '0.78rem', color: '#3b82f6', marginTop: '2px' }}>📍 {data.entranceDirection}</div>
                      )}
                    </>
                  )}
                  {/* Door direction indicator */}
                  <div style={{ marginTop: '8px' }}>
                    <EntranceDirectionIndicator suggestedEntrance={data.suggestedEntrance} size="md" />
                  </div>
                </div>
              </div>
            )}

            {/* Entry Guidance (zone-level fallback) */}
            {!data.suggestedEntrance && data.entryGuidance && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '14px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px' }}>
                <span style={{ fontSize: '1rem', flexShrink: 0 }}>🚪</span>
                <span style={{ fontSize: '0.82rem', color: '#92400e', lineHeight: 1.4 }}>{data.entryGuidance}</span>
              </div>
            )}

            {/* Show Map button + Mini Floorplan */}
            {data.gridAddress && (
              <div style={{ marginTop: '14px', textAlign: 'center' }}>
                <button
                  onClick={() => setShowMap(!showMap)}
                  style={{ background: 'none', border: `1px solid ${showMap ? '#1a6b3c' : '#d1d5db'}`, borderRadius: '8px', padding: '8px 16px', fontSize: '0.82rem', fontWeight: 600, color: showMap ? '#1a6b3c' : '#374151', cursor: 'pointer', transition: 'all 0.2s' }}
                  aria-expanded={showMap}
                  aria-label="Show venue map with your seat"
                >
                  {showMap ? '🗺️ Hide Map' : '🗺️ Show My Seat on Map'}
                </button>
                {showMap && (
                  <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
                    <MiniFloorplanMap highlightedGridAddress={data.gridAddress} suggestedEntrance={data.suggestedEntrance} />
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Footer */}
          <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
            <button onClick={handleReset} style={{ background: 'none', border: 'none', color: '#1a6b3c', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}>← Search Again</button>
          </div>

          </div>{/* end content wrapper */}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <img src="/KDULogo.PNG" alt="KDU" style={styles.logo} />
          <div style={styles.eventTitle}>KDU Georgia 2026</div>
          <div style={styles.eventSubtitle}>Find My Info</div>
        </div>

        <div style={{ padding: '24px 20px' }}>
          <p style={{ fontSize: '0.9rem', color: '#666', textAlign: 'center', marginBottom: '20px' }}>
            Enter your ticket number or order number to find your seating and event details.
          </p>

          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', justifyContent: 'center' }}>
            <button style={searchMode === 'ticket' ? styles.modeActive : styles.modeBtn} onClick={() => { setSearchMode('ticket'); setOrderGuests([]); setError(''); }}>🎫 Ticket #</button>
            <button style={searchMode === 'order' ? styles.modeActive : styles.modeBtn} onClick={() => { setSearchMode('order'); setOrderGuests([]); setError(''); }}>📦 Order #</button>
            <button style={searchMode === 'name' ? styles.modeActive : styles.modeBtn} onClick={() => { setSearchMode('name'); setOrderGuests([]); setError(''); }}>👤 Name</button>
          </div>

          {/* Search input */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              style={styles.input}
              type="text"
              placeholder={searchMode === 'ticket' ? 'e.g. TKT-042 or 42' : searchMode === 'order' ? 'e.g. 19836676' : 'e.g. Thomas'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              aria-label={searchMode === 'ticket' ? 'Ticket number' : searchMode === 'order' ? 'Order number' : 'Guest name'}
            />
            <button style={styles.searchBtn} onClick={handleSearch} disabled={loading || !searchTerm.trim()}>
              {loading ? '...' : 'Find'}
            </button>
          </div>

          {error && <p style={{ color: '#d32f2f', fontSize: '0.85rem', textAlign: 'center', marginTop: '12px' }}>{error}</p>}

          {/* Order guest selection */}
          {orderGuests.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '10px', textAlign: 'center' }}>Select your name:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {orderGuests.map((g) => (
                  <button key={g.ticketNumber} style={styles.guestPickBtn} onClick={() => handleSelectGuest(g.ticketNumber)}>
                    <span style={{ fontWeight: 600 }}>{g.fullName}</span>
                    <span style={{ fontSize: '0.8rem', color: '#1a6b3c' }}>{g.ticketNumber}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'linear-gradient(135deg, #0f2d1f 0%, #1a4a2e 50%, #0d2418 100%)', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', position: 'relative' as const, overflow: 'hidden' },
  card: { width: '100%', maxWidth: '420px', backgroundColor: '#fff', borderRadius: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden', position: 'relative' as const, zIndex: 1 },
  header: { textAlign: 'center' as const, padding: '24px 20px 16px', background: 'linear-gradient(135deg, #1a6b3c, #0f4a28)', color: '#fff' },
  logo: { width: '60px', height: '60px', borderRadius: '12px', marginBottom: '8px', objectFit: 'contain' as const },
  eventTitle: { fontSize: '1.1rem', fontWeight: 700, letterSpacing: '0.5px' },
  eventSubtitle: { fontSize: '0.85rem', opacity: 0.85, marginTop: '2px' },
  nameSection: { textAlign: 'center' as const, padding: '20px 20px 12px', borderBottom: '1px solid #e5e7eb' },
  guestName: { fontSize: '1.5rem', fontWeight: 800, color: '#0f2d1f', marginBottom: '6px' },
  ticketBadge: { display: 'inline-block', padding: '4px 14px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600, color: '#166534' },
  infoGrid: { padding: '16px 20px', display: 'flex', flexDirection: 'column' as const, gap: '14px' },
  infoRow: { display: 'flex', alignItems: 'flex-start', gap: '12px' },
  infoIcon: { fontSize: '1.3rem', lineHeight: 1.4, flexShrink: 0 },
  infoLabel: { fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
  infoValue: { fontSize: '1rem', fontWeight: 600, color: '#111827', marginTop: '1px' },
  footer: { textAlign: 'center' as const, padding: '16px 20px 20px', borderTop: '1px solid #e5e7eb' },
  resetBtn: { background: 'none', border: 'none', color: '#1a6b3c', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', padding: '8px 16px' },
  input: { flex: 1, padding: '14px 16px', border: '2px solid #e0e0e0', borderRadius: '12px', fontSize: '1rem', outline: 'none' },
  searchBtn: { padding: '14px 20px', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', minWidth: '70px' },
  modeBtn: { padding: '8px 16px', border: '2px solid #e0e0e0', borderRadius: '20px', background: '#fff', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer', color: '#666' },
  modeActive: { padding: '8px 16px', border: '2px solid #1a6b3c', borderRadius: '20px', background: '#f0fdf4', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', color: '#166534' },
  guestPickBtn: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', border: '1px solid #e0e0e0', borderRadius: '10px', background: '#fff', cursor: 'pointer', width: '100%', fontSize: '0.95rem' },
};

// QR Code display component for check-in scanning
function QrCodeDisplay({ registrationId }: { registrationId: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const payload = JSON.stringify({ type: 'guest', identifier: registrationId });
    QRCode.toCanvas(canvasRef.current, payload, {
      width: 200,
      margin: 2,
      color: { dark: '#1a6b3c', light: '#ffffff' },
    }).catch(() => {});
  }, [registrationId]);

  return (
    <div style={{ textAlign: 'center', padding: '16px 0', borderTop: '1px solid #f0f0f0', marginTop: '12px' }}>
      <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '8px' }}>Show this to check-in staff</div>
      <canvas ref={canvasRef} style={{ display: 'block', margin: '0 auto', borderRadius: '8px', border: '2px solid #1a6b3c' }} />
      <div style={{ fontSize: '0.72rem', color: '#999', marginTop: '6px' }}>Scan to check in</div>
    </div>
  );
}
